import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import * as Types from '../../services/api/api.types';
import { useCart } from '../../contexts/CartContext';
import { useNotification } from '../../contexts/NotificationContext';

interface ProductCardProps {
    product: Types.ProductFull;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
    const { addToCart } = useCart();
    const { showNotification } = useNotification();

    const isProductAvailable = (): boolean => {
        return product.status === 1;
    };

    const getProductName = (): string => {
        if (product.name && Array.isArray(product.name) && product.name.length > 0) {
            const bgName = product.name.find(n => n && n.language_code === 'bg');
            if (bgName && bgName.text) {
                return bgName.text;
            }
        }

        // In case of no product name
        if (product.reference_number) {
            // Formatting ref number
            // "ABC-DEF-12345" -> "12345"
            const parts = product.reference_number.split('-');
            if (parts.length > 1) {
                return parts[parts.length - 1];
            }
            return product.reference_number;
        }

        return product.model || 'Продукт';
    };

    const formatPrice = (priceInEUR: number | string): string => {
        const price = typeof priceInEUR === 'string' ? parseFloat(priceInEUR) : priceInEUR;
        return `€${price.toFixed(2)}`;
    };

    const handleAddToCart = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (!isProductAvailable()) {
            return; 
        }
        const productName = getProductName();

        addToCart({
            id: product.id,
            supplier: product.supplier ?? 'vali',
            slug: product.slug ?? null,
            referenceNumber: product.reference_number,
            name: productName,
            price: Number(product.price_partner),
            pricePromo: product.price_promo ? Number(product.price_promo) : null,
            quantity: 1,
            image: product.images && product.images.length > 0 ? product.images[0].href : null
        });

        if (showNotification) {
            showNotification('success', `${productName} е добавен в количката`, 3000);
        }
    };

    const regularPrice = formatPrice(Number(product.price_partner));
    const promoPrice = product.price_promo ? formatPrice(Number(product.price_promo)) : null;

    return (
        <Link
            to={`/product/${product.slug ?? product.id}`}
            className="group flex flex-col h-full border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
        >
            <div className="relative w-full h-64 bg-gray-100 overflow-hidden">
                {product.images && product.images.length > 0 ? (
                    <img
                        src={product.images[0].href}
                        loading="lazy"
                        decoding="async"
                        alt={getProductName()}
                        className="absolute inset-0 w-full h-full object-contain p-4"
                    />
                ) : (
                    <div className="flex items-center justify-center h-full">
                        <span className="text-gray-400">Няма изображение</span>
                    </div>
                )}

                {product.price_promo !== null && (
                    <div className="absolute top-2 right-2 bg-[#31B823] text-white text-xs font-bold py-1 px-2 rounded">
                        ПРОМО
                    </div>
                )}
            </div>

            <div className="p-4 flex-grow flex flex-col">
                <div className="flex-grow">
                    <h3 className="font-medium text-sm mb-2 line-clamp-2">{getProductName()}</h3>
                </div>

                <div className="mt-auto">
                    <div className="flex justify-between items-center mb-3">
                        <div className="flex flex-col">
                            {promoPrice && product.price_promo && product.price_promo > 1 ? (
                                <>
                                    <div className="flex flex-col">
                                        <span className="text-[#31B823] font-bold text-sm">
                                            {promoPrice}
                                        </span>
                                        <span className="text-gray-500 text-xs line-through">
                                            {regularPrice}
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <span className="font-bold text-sm">
                                    {regularPrice}
                                </span>
                            )}
                        </div>

                        <div className="text-xs text-gray-500">
                            {product.manufacturer || ''}
                        </div>
                    </div>

                    <button
                        onClick={handleAddToCart}
                        disabled={!isProductAvailable()}
                        className={`w-full py-1 px-3 rounded text-sm flex items-center justify-center transition-colors ${isProductAvailable()
                                ? 'bg-[#31B823] hover:bg-[#28A01D] text-white cursor-pointer'
                                : 'bg-gray-300 text-gray-700 cursor-not-allowed'
                            }`}
                    >
                        {isProductAvailable() ? (
                            <>
                                <ShoppingCart className="h-4 w-4 mr-1" />
                                Добави
                            </>
                        ) : (
                            'Няма наличност'
                        )}
                    </button>
                </div>
            </div>
        </Link>
    );
};

export default ProductCard;